import { useCallback, useMemo, useState } from 'react';
import { ALL_COLUMNS, BUILTIN_PRESETS, COLUMN_BY_ID, ColumnDef } from './adColumns';

// Persists the Fetched Ads table's column layout (order + visibility) and the
// user's named presets to localStorage. Built-in presets live in code and are
// always available; user presets are stored locally and editable.

const STORAGE_KEY = 'sbly.adsTable.v1';

export interface Preset {
  name: string;
  columnIds: string[];
  builtIn?: boolean;
}

interface PersistedState {
  layout: string[];                 // ordered ids of currently-visible columns
  userPresets: { name: string; columnIds: string[] }[];
  activePreset: string | null;      // null = "Custom" (unsaved edits)
}

const VALID_IDS = new Set(ALL_COLUMNS.map(c => c.id));

// Drop ids that no longer exist (e.g. a column renamed between releases).
function sanitize(ids: unknown): string[] {
  if (!Array.isArray(ids)) return [];
  return ids.filter((id): id is string => typeof id === 'string' && VALID_IDS.has(id));
}

function loadState(): PersistedState {
  const fallback: PersistedState = {
    layout: BUILTIN_PRESETS[0].columnIds.slice(),
    userPresets: [],
    activePreset: BUILTIN_PRESETS[0].name,
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    const layout = sanitize(parsed.layout);
    return {
      layout: layout.length > 0 ? layout : fallback.layout,
      userPresets: Array.isArray(parsed.userPresets)
        ? parsed.userPresets
            .filter((p: any) => p && typeof p.name === 'string')
            .map((p: any) => ({ name: p.name, columnIds: sanitize(p.columnIds) }))
        : [],
      activePreset: typeof parsed.activePreset === 'string' ? parsed.activePreset : null,
    };
  } catch {
    return fallback;
  }
}

export default function useColumnPreferences() {
  const [state, setState] = useState<PersistedState>(loadState);

  const persist = useCallback((next: PersistedState) => {
    setState(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore quota */ }
  }, []);

  const visibleColumns = useMemo<ColumnDef[]>(
    () => state.layout.map(id => COLUMN_BY_ID[id]).filter(Boolean),
    [state.layout],
  );

  const presets = useMemo<Preset[]>(() => [
    ...BUILTIN_PRESETS.map(p => ({ ...p, builtIn: true })),
    ...state.userPresets,
  ], [state.userPresets]);

  // Editing the layout drops the active-preset link — it becomes "Custom".
  const toggleColumn = useCallback((id: string) => {
    const layout = state.layout.includes(id)
      ? state.layout.filter(c => c !== id)
      : [...state.layout, id];
    persist({ ...state, layout, activePreset: null });
  }, [state, persist]);

  const reorder = useCallback((from: number, to: number) => {
    if (from === to) return;
    const layout = state.layout.slice();
    const [moved] = layout.splice(from, 1);
    layout.splice(to, 0, moved);
    persist({ ...state, layout, activePreset: null });
  }, [state, persist]);

  const applyPreset = useCallback((name: string) => {
    const preset = [...BUILTIN_PRESETS, ...state.userPresets].find(p => p.name === name);
    if (!preset) return;
    persist({ ...state, layout: preset.columnIds.slice(), activePreset: name });
  }, [state, persist]);

  // Save / overwrite a user preset with the current layout. Built-in names
  // are protected.
  const savePreset = useCallback((name: string) => {
    const trimmed = name.trim();
    if (!trimmed || BUILTIN_PRESETS.some(p => p.name === trimmed)) return;
    const userPresets = state.userPresets.filter(p => p.name !== trimmed);
    userPresets.push({ name: trimmed, columnIds: state.layout.slice() });
    persist({ ...state, userPresets, activePreset: trimmed });
  }, [state, persist]);

  const deletePreset = useCallback((name: string) => {
    if (BUILTIN_PRESETS.some(p => p.name === name)) return;
    persist({
      ...state,
      userPresets: state.userPresets.filter(p => p.name !== name),
      activePreset: state.activePreset === name ? null : state.activePreset,
    });
  }, [state, persist]);

  return {
    visibleColumns,
    visibleIds: state.layout,
    activePreset: state.activePreset,
    presets,
    toggleColumn,
    reorder,
    applyPreset,
    savePreset,
    deletePreset,
  };
}
